import React, { type FC, useCallback, useState, useEffect } from 'react';

import { log } from '@atlassian/jira-common-util-logging';
import { useIsModal, useIssueId, useIssueKey } from '@atlassian/jira-issue-context-service';
import type { CreatedIssueWithIssueData } from '@atlassian/jira-issue-create-common-types';
import {
	GIC_LIFECYCLE_EVENTS,
	useGICLifeCycleEvents,
} from '@atlassian/jira-issue-create-extensibility';
import type { ChildIssue } from '@atlassian/jira-issue-shared-types';
import { sendExperienceAnalytics } from '@atlassian/jira-issue-view-analytics';
import { GIC_CALLBACK_PAYLOAD_AI_BREAKDOWN_CREATE_ID } from '@atlassian/jira-issue-view-common-constants';
import type { ChildIssueType } from '@atlassian/jira-issue-view-common-types/src/child-issue-type';
import { useFetchSuggestedChildIssues } from '@atlassian/jira-issue-view-services/src/child-issues/use-fetch-suggested-child-issues';
import type { SuggestedChildIssuesGiraResponse } from '@atlassian/jira-issue-view-services/src/child-issues/use-fetch-suggested-child-issues/types';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import { useIsMounted } from '@atlassian/jira-platform-react-hooks-use-is-mounted';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useProjectId,
	useProjectKey,
	useEdition,
	useProjectType,
	useApplication,
} from '@atlassian/jira-project-context-service';
import { useDefaultPriority } from '../../services/use-default-priority';
import { useDefaultStatus } from '../../services/use-default-status';
import {
	type SuggestedChildIssue,
	steps,
	useIssueBreakdown,
	reasons,
	DEFAULT_SELECTED_ISSUE_INDEX,
} from './context';

import { IssueBreakdownDraftList } from './ui/issue-breakdown-draft-list';
import { IssueBreakdownError } from './ui/issue-breakdown-error';
import { IssueBreakdownPrompt } from './ui/issue-breakdown-prompt';
import { getIssueCreateModalPayload } from './utils';

export type Props = {
	onCreate: (createdChild: CreatedIssueWithIssueData | ChildIssue, extraArguments: {}) => void;
	childIssueTypes: ChildIssueType[];
	isSubTaskCreationOpen: boolean;
	issueHierarchyLevel?: number | undefined;
};

// visible for testing
export const useIsLoading = () => useState(false);

export const AiIssueBreakdown: FC<Props> = ({
	onCreate,
	childIssueTypes,
	isSubTaskCreationOpen,
	issueHierarchyLevel,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isLoading, setIsLoading] = useIsLoading();

	const [
		{ currentStep, suggestedChildIssues, promptValue, selectedIssueIndex },
		{
			setIssueBreakdownStep,
			setPromptValue,
			setIssueTypeIds,
			setHeaderPromptValue,
			resetIssueBreakdownState,
			updateSuggestedResult,
			removeCurrentSuggestedChildIssue,
			unselectCurrentIndex,
			updateStreamingStatus,
			getSuggestedLength,
		},
	] = useIssueBreakdown();

	const issueKey = useIssueKey();
	const parentIssueId = useIssueId() || '';

	const projectKey = useProjectKey(issueKey);
	const edition = useEdition(projectKey, true);
	const projectType = useProjectType(projectKey) || null;
	const projectId = useProjectId(projectKey);
	const application = useApplication(projectKey, true);
	const isIssueModalView = useIsModal();
	const [{ isModalOpen, minimizableModalView }, { openIssueCreateModal, closeIssueCreateModal }] =
		useTriggerIssueCreateModal();
	const { subscribe } = useGICLifeCycleEvents();
	const [selectedIssueType, setSelectedIssueType] = useState<string | undefined>();
	const [{ data: defaultStatus }, fetchDefaultStatus] = useDefaultStatus();
	const [{ data: defaultPriority }, fetchDefaultPriority] = useDefaultPriority();

	/**
	 * Only close issue create modal for draft list step in case mini modal is in use elsewhere.
	 */
	const closeIssueBreakdownModal = useCallback(() => {
		const isAiView =
			minimizableModalView === 'ai-enabled' || minimizableModalView === 'ai-enabled-minimized';
		if (isModalOpen && isAiView) {
			closeIssueCreateModal(false);
		}
	}, [closeIssueCreateModal, isModalOpen, minimizableModalView]);

	const unselectAndCloseCurrentIssue = useCallback(() => {
		closeIssueBreakdownModal();
		unselectCurrentIndex();
	}, [closeIssueBreakdownModal, unselectCurrentIndex]);

	const openIssueBreakdownCreateModal = useCallback(
		(childIssue: SuggestedChildIssue) => {
			if (!projectId || !parentIssueId) {
				log.safeErrorWithoutCustomerData(
					'issue.issue-view-services.src.child-issues.use-fetched-suggested-child-issuses',
					'Error fetching suggested child issues, parentIssueId or projectId not',
				);
				return;
			}

			openIssueCreateModal(
				getIssueCreateModalPayload({
					childIssue,
					projectId,
					parentIssueId,
					childIssueTypes,
					isSubTaskCreationOpen,
				}),
			);
		},
		[childIssueTypes, isSubTaskCreationOpen, openIssueCreateModal, parentIssueId, projectId],
	);

	const onFetchSuggestedIssuesSuccess = useCallback(
		(response: SuggestedChildIssuesGiraResponse) => {
			setIsLoading(false);
			setIssueBreakdownStep(steps.draftListStep);
			setPromptValue(''); // clear the prompt if fetching was successful

			const suggestedChildIssuesData = response.suggestedChildIssues;
			if (suggestedChildIssuesData.length > 0) {
				updateSuggestedResult(response);
				const selectedIssue = suggestedChildIssuesData[DEFAULT_SELECTED_ISSUE_INDEX];
				openIssueBreakdownCreateModal(selectedIssue);
			}
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiIssueBreakdown suggested', {
				suggestedChildIssues: response.suggestedChildIssues.length,
			});
		},
		[
			setIsLoading,
			setIssueBreakdownStep,
			setPromptValue,
			createAnalyticsEvent,
			updateSuggestedResult,
			openIssueBreakdownCreateModal,
		],
	);

	const onStreamComplete = useCallback(() => {
		fireTrackAnalytics(createAnalyticsEvent({}), 'aiIssueBreakdown suggested', {
			suggestedChildIssues: getSuggestedLength(),
		});
		sendExperienceAnalytics({
			experience: 'aiIssueBreakdown',
			wasExperienceSuccesful: true,
			analyticsSource: 'jiraSuggestedChildIssue',
			application: application ?? null,
			edition: edition ?? null,
		});
	}, [application, createAnalyticsEvent, edition, getSuggestedLength]);

	const onFetchSuggestedIssuesFailure = useCallback(() => {
		setIssueBreakdownStep('errorStep');
		setIsLoading(false);
	}, [setIsLoading, setIssueBreakdownStep]);

	const { cancelFetchSuggestedChildIssues, isFetchingSuggestedChildIssues } =
		useFetchSuggestedChildIssues(onFetchSuggestedIssuesSuccess, onFetchSuggestedIssuesFailure);

	/**
	 * Reset and close issue breakdown whenever it is cancelled.
	 */
	const cancelIssueBreakdown = useCallback(() => {
		if (isLoading) {
			cancelFetchSuggestedChildIssues();
			setIsLoading(false);
		}
		if (isLoading || suggestedChildIssues.length <= 1) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiIssueBreakdown cancelled', {});
		} else {
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiIssueBreakdown discardedAll', {});
		}
		closeIssueBreakdownModal();
		resetIssueBreakdownState();
	}, [
		cancelFetchSuggestedChildIssues,
		closeIssueBreakdownModal,
		isLoading,
		resetIssueBreakdownState,
		setIsLoading,
		createAnalyticsEvent,
		suggestedChildIssues.length,
	]);

	const onSubmitFetchSuggestedChildIssues = useCallback(
		(submittedIssueTypeIds?: string[]) => {
			if (isFetchingSuggestedChildIssues) {
				return;
			}

			fireTrackAnalytics(createAnalyticsEvent({}), 'aiInteraction initiated', {
				aiFeatureName: 'intelligentWorkBreakdown',
				proactiveAIGenerated: 0,
				userGeneratedAI: 1,
				edition,
			});

			// Only change the header prompt if there's a prompt value
			if (promptValue) {
				setHeaderPromptValue(promptValue);
			}

			submittedIssueTypeIds && setIssueTypeIds(submittedIssueTypeIds);

			updateStreamingStatus(true);
			currentStep !== steps.draftListStep && setIssueBreakdownStep(steps.draftListStep);

			setSelectedIssueType(submittedIssueTypeIds?.[0]);
		},
		[
			createAnalyticsEvent,
			currentStep,
			edition,
			isFetchingSuggestedChildIssues,
			promptValue,
			setHeaderPromptValue,
			setIssueBreakdownStep,
			setIssueTypeIds,
			updateStreamingStatus,
		],
	);

	const openNextSuggestedIssue = useCallback(() => {
		const hasNoSuggestions = suggestedChildIssues.length <= 1;
		const isNoIssueSelected = selectedIssueIndex === null;

		if (hasNoSuggestions || isNoIssueSelected) {
			cancelIssueBreakdown();
			return;
		}
		const nextChildIssueIndex = (selectedIssueIndex + 1) % suggestedChildIssues.length;

		openIssueBreakdownCreateModal(suggestedChildIssues[nextChildIssueIndex]);
	}, [
		cancelIssueBreakdown,
		openIssueBreakdownCreateModal,
		selectedIssueIndex,
		suggestedChildIssues,
	]);

	const onSubmitCreateIssue = useCallback(
		(childIssue: CreatedIssueWithIssueData, wasFormDirty: boolean | undefined) => {
			// Just emitting event on create for now
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiIssueBreakdown created', {
				wasFormDirty,
			});
			openNextSuggestedIssue();
			removeCurrentSuggestedChildIssue(reasons.accept);
			onCreate(childIssue, {
				wasFormDirty,
				wasIwbUsed: true,
			});
		},
		[createAnalyticsEvent, onCreate, removeCurrentSuggestedChildIssue, openNextSuggestedIssue],
	);

	useEffect(() => {
		// When the parent issue has just 1 issue type in the hierarchy below it,
		// then clicking the Suggest issues button directly starts suggesting issues
		if (childIssueTypes.length === 1 && currentStep === steps.promptInputStep) {
			const issueTypeId = childIssueTypes.map((item) => item.id);
			onSubmitFetchSuggestedChildIssues(issueTypeId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [childIssueTypes.length, currentStep, fireTrackAnalytics]);

	useEffect(() => {
		// When user selects the child issue, trigger getting the default status
		selectedIssueType && fetchDefaultStatus(selectedIssueType);
		selectedIssueType && fetchDefaultPriority(selectedIssueType);
	}, [selectedIssueType, fetchDefaultStatus, fetchDefaultPriority]);

	useEffect(
		() =>
			subscribe?.(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_ALL_SUGGESTED_ISSUES, async () => {
				cancelIssueBreakdown();
			}),
		[cancelIssueBreakdown, subscribe],
	);

	useEffect(
		() =>
			subscribe?.(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_SUGGESTED_ISSUE_CHANGES, async () => {
				selectedIssueIndex !== null &&
					openIssueBreakdownCreateModal(suggestedChildIssues[selectedIssueIndex]);
			}),
		[suggestedChildIssues, openIssueBreakdownCreateModal, subscribe, selectedIssueIndex],
	);

	useEffect(
		() =>
			subscribe?.(GIC_LIFECYCLE_EVENTS.ISSUE_CREATE_SUCCESS, async (payload) => {
				if (
					payload.callbackPayload?.id !== GIC_CALLBACK_PAYLOAD_AI_BREAKDOWN_CREATE_ID ||
					!payload.createdIssueData
				)
					return;

				const wasFormDirty = payload.callbackPayload.data?.wasFormDirty ?? false;
				onSubmitCreateIssue(payload.createdIssueData, Boolean(wasFormDirty));
			}),
		[onSubmitCreateIssue, subscribe],
	);

	useEffect(
		() =>
			subscribe?.(GIC_LIFECYCLE_EVENTS.MODAL_DISCARD_SUGGESTED_ISSUE, async () => {
				if (selectedIssueIndex !== null) {
					openNextSuggestedIssue();
					removeCurrentSuggestedChildIssue(reasons.reject);
					fireTrackAnalytics(createAnalyticsEvent({}), 'aiIssueBreakdown discarded', {});
				}
			}),
		[
			closeIssueCreateModal,
			onSubmitCreateIssue,
			removeCurrentSuggestedChildIssue,
			openNextSuggestedIssue,
			selectedIssueIndex,
			subscribe,
			suggestedChildIssues,
			createAnalyticsEvent,
		],
	);

	const mounted = useIsMounted();

	/**
	 * When leaving a page (on unmount) we need to close issue breakdown and reset its state
	 */
	useEffect(
		() => () => {
			if (!mounted.current) {
				unselectAndCloseCurrentIssue();
			}
		},
		[mounted, unselectAndCloseCurrentIssue],
	);

	useEffect(() => {
		if (currentStep === steps.promptInputStep) {
			// When the prompt is shown, we meassure the experience being served
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiIssueBreakdown served', {});
		}
	}, [currentStep, createAnalyticsEvent]);

	useEffect(() => {
		// Auto trigger GIC dialog using first item after streaming complete
		if (currentStep === steps.draftListStep && selectedIssueIndex != null && !isModalOpen) {
			openIssueBreakdownCreateModal(suggestedChildIssues[selectedIssueIndex]);
		}
	}, [
		currentStep,
		isModalOpen,
		openIssueBreakdownCreateModal,
		selectedIssueIndex,
		suggestedChildIssues,
	]);

	if (isIssueModalView) return null;

	switch (currentStep) {
		case steps.promptInputStep:
			return (
				<IssueBreakdownPrompt
					isLoading={isLoading}
					onSubmit={onSubmitFetchSuggestedChildIssues}
					onCancel={cancelIssueBreakdown}
					childIssueTypes={childIssueTypes}
					issueHierarchyLevel={issueHierarchyLevel}
				/>
			);
		case steps.draftListStep:
			return (
				<IssueBreakdownDraftList
					isLoading={isLoading}
					projectType={projectType}
					childIssueTypes={childIssueTypes}
					defaultStatus={defaultStatus}
					defaultPriority={defaultPriority}
					items={suggestedChildIssues}
					onSubmit={onSubmitFetchSuggestedChildIssues}
					onChangePrompt={setPromptValue}
					onDiscard={cancelIssueBreakdown}
					onClickItem={openIssueBreakdownCreateModal}
					onFocusPrompt={unselectAndCloseCurrentIssue}
					onStreamComplete={onStreamComplete}
					issueHierarchyLevel={issueHierarchyLevel}
				/>
			);
		case steps.errorStep:
			return (
				<IssueBreakdownError
					onRetry={onSubmitFetchSuggestedChildIssues}
					onCancel={cancelIssueBreakdown}
					issueHierarchyLevel={issueHierarchyLevel}
				/>
			);
		case steps.defaultStep:
		default:
			return null;
	}
};
